import * as React from 'react'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { debounce } from '@mui/material/utils'
import { CircularProgress, InputAdornment } from '@mui/material'
import { AssemblyLine } from '@assembly/pages/AssemblyLines/types'
import { getAssemblyLine, getAssemblyLines } from '@assembly/api/assemblyLine'
import AccountTreeIcon from '@mui/icons-material/AccountTree'

type AssemblyLineSearchAndSelectProps = {
  onChange: (assemblyId: string) => void
  value: string
  error?: boolean
  helperText?: string
  label?: string
  placeholder?: string
  required?: boolean
}

export default function AssemblyLineSearchAndSelect({
  onChange,
  value,
  error,
  helperText,
  label = 'Assembly Line',
  placeholder = 'Search assembly lines',
  required = false,
}: AssemblyLineSearchAndSelectProps) {
  const [inputValue, setInputValue] = React.useState('')
  const [options, setOptions] = React.useState<AssemblyLine[]>([])
  const [loading, setLoading] = React.useState<boolean>(false)
  const [valueOption, setValueOption] = React.useState<AssemblyLine | null>(null)

  const fetch = React.useMemo(
    () =>
      debounce(
        (request: { input: string }, callback: (results?: AssemblyLine[]) => void) => {
          if (request.input.length) {
            getAssemblyLinesAsync(request.input, callback)
          } else {
            callback([])
          }
        },
        400
      ),
    []
  )

  const getAssemblyLinesAsync = async (search: string, callback: (results: AssemblyLine[]) => void) => {
    try {
      setLoading(true)
      const { data } = await getAssemblyLines({
        search,
        limit: 20,
        offset: 0,
      })
      callback(data.data || [])
      setLoading(false)
    } catch (err) {
      console.error(err)
      callback([])
      setLoading(false)
    }
  }

  React.useEffect(() => {
    fetch({ input: inputValue }, (results?: AssemblyLine[]) => {
      setOptions(results || [])
    })
  }, [inputValue, fetch])

  // Fetch assembly line when value (id) is set to display in the input
  React.useEffect(() => {
    if (!value) {
      setValueOption(null)
      return
    }
    if (valueOption?.id === value) {
      return
    }
    let cancelled = false
    const fetchAssemblyLine = async () => {
      try {
        const { data } = await getAssemblyLine(value)
        if (!cancelled) {
          setValueOption(data)
        }
      } catch {
        if (!cancelled) {
          setValueOption(null)
        }
      }
    }
    fetchAssemblyLine()
    return () => {
      cancelled = true
    }
  }, [value, valueOption?.id])

  return (
    <Autocomplete
      sx={{ margin: 0 }}
      fullWidth
      loading={loading}
      getOptionLabel={(option) => `${option.name} (${option.id})`}
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      value={valueOption}
      noOptionsText="No assembly lines found"
      onChange={(_: any, newValue: AssemblyLine | null) => {
        onChange(newValue?.id || '')
        setValueOption(newValue)
      }}
      onInputChange={(_, newInputValue) => {
        setInputValue(newInputValue)
      }}
      isOptionEqualToValue={(option, val) => option.id === val?.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          fullWidth
          required={required}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" sx={{ marginLeft: '5px' }}>
                <AccountTreeIcon sx={{ width: 20, height: 20 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
          error={error}
          helperText={helperText}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <Grid container alignItems="center">
            <Grid
              item
              sx={{ width: '100%', wordWrap: 'break-word' }}
            >
              <Box component="span" sx={{ fontWeight: 'bold' }}>
                {option.name}
              </Box>
              <Typography variant="body2" color="text.secondary" display="block">
                {option.id}
              </Typography>
            </Grid>
          </Grid>
        </li>
      )}
    />
  )
}
